import React, { type ReactElement } from 'react'
import mapboxgl from 'mapbox-gl'
import { createRoot } from 'react-dom/client'

import { type GeoPoint } from '~types'

const PopupContentWrapper = ({ children }: { children: React.ReactNode }): ReactElement => {
  return <div className='map-popup-content'>{children}</div>
}

const MAX_POPUP_CASES = 10
// Clusters can cover far more locations than a popup can usefully list
const MAX_CLUSTER_LEAVES = 250

const ScrollableList = ({ cases }: { cases: ExtendedGeoPoint[] }): ReactElement => {
  const displayed = cases.slice(0, MAX_POPUP_CASES)
  const remaining = cases.length - displayed.length

  return (
    <div style={{ maxHeight: 400, overflowY: 'auto' }}>
      {displayed.map((caseItem, index) => (
        <div
          key={index}
          style={{
            borderBottom: index < displayed.length - 1 ? '1px solid #eee' : 'none',
          }}
        >
          {caseItem.popupContent}
        </div>
      ))}
      {remaining > 0 && (
        <div
          style={{
            padding: '12px 14px',
            fontSize: 13,
            color: '#666',
            textAlign: 'center',
            fontFamily: 'Albert Sans, sans-serif',
          }}
        >
          +{remaining} more location{remaining > 1 ? 's' : ''}
        </div>
      )}
    </div>
  )
}

export interface ExtendedGeoPoint extends GeoPoint {
  popupContent?: React.ReactNode | null
  caseCount?: number
  customData?: Record<string, unknown>
}

const POPUP_OPTIONS: mapboxgl.PopupOptions = {
  closeButton: false,
  closeOnClick: true,
  maxWidth: '420px',
  className: 'custom-popup cases-popup',
}

let currentPopup: mapboxgl.Popup | null = null
// Which map and source the open popup describes, so removing one source's points
// never dismisses a popup a user opened on an unrelated map or layer
let currentPopupOwner: { map: mapboxgl.Map; sourceId?: string } | null = null

export const closeCurrentPopup = (): void => {
  currentPopup?.remove()
  currentPopup = null
  currentPopupOwner = null
}

export const closeCurrentPopupForSource = (map: mapboxgl.Map, sourceId: string): void => {
  if (currentPopupOwner?.map !== map || currentPopupOwner.sourceId !== sourceId) return
  closeCurrentPopup()
}

const openPopup = (
  map: mapboxgl.Map,
  content: React.ReactNode,
  coordinates: [number, number],
  sourceId?: string,
): void => {
  const popupNode = document.createElement('div')
  const root = createRoot(popupNode)
  root.render(<PopupContentWrapper>{content}</PopupContentWrapper>)

  currentPopup?.remove()
  currentPopupOwner = { map, sourceId }

  const point = map.project(coordinates as mapboxgl.LngLatLike)
  const { clientWidth, clientHeight } = map.getContainer()
  const vertical = point.y < clientHeight / 2 ? 'top' : 'bottom'
  const horizontal =
    point.x < clientWidth / 3 ? '-left' : point.x > (clientWidth * 2) / 3 ? '-right' : ''
  const anchor = `${vertical}${horizontal}` as mapboxgl.Anchor

  currentPopup = new mapboxgl.Popup({ ...POPUP_OPTIONS, anchor })
    .setLngLat(coordinates)
    .setDOMContent(popupNode)
    .on('close', () => root.unmount())
    .addTo(map)

  currentPopup.getElement()?.style.setProperty('z-index', '20')
}

export const showMultiCasePopup = (
  map: mapboxgl.Map,
  cases: ExtendedGeoPoint[],
  coordinates: [number, number],
  sourceId?: string,
): void => {
  if (cases.length === 0) return
  openPopup(map, <ScrollableList cases={cases} />, coordinates, sourceId)
}

const showPointPopup = (
  map: mapboxgl.Map,
  point: ExtendedGeoPoint,
  coordinates: [number, number],
  sourceId?: string,
): void => {
  if (!point.popupContent) return
  openPopup(map, point.popupContent, coordinates, sourceId)
}

type LayerClickHandler = (e: mapboxgl.MapMouseEvent) => void

// Click handlers registered per map and source id, so re-rendering a source's
// points never leaves stale listeners (closing over an outdated points array)
// attached, and concurrent maps using the same source id do not collide.
const clusteredClickHandlers = new WeakMap<mapboxgl.Map, Map<string, LayerClickHandler[]>>()

const registerClusteredClickHandlers = (
  map: mapboxgl.Map,
  sourceId: string,
  handlers: LayerClickHandler[],
): void => {
  const handlersBySourceId = clusteredClickHandlers.get(map) ?? new Map()
  handlersBySourceId.set(sourceId, handlers)
  clusteredClickHandlers.set(map, handlersBySourceId)
}

const removeClusteredClickHandlers = (map: mapboxgl.Map, sourceId: string): void => {
  const handlersBySourceId = clusteredClickHandlers.get(map)
  const handlers = handlersBySourceId?.get(sourceId)
  if (!handlersBySourceId || !handlers) return
  handlersBySourceId.delete(sourceId)
  const [pointHandler, clusterHandler] = handlers
  try {
    map.off('click', `${sourceId}-unclustered-point`, pointHandler)
    map.off('click', `${sourceId}-clusters`, clusterHandler)
  } catch {
    // map may already be destroyed
  }
}

export const removeClusteredPointsFromMap = (
  map: mapboxgl.Map,
  sourceId: string = 'clustered-points',
): void => {
  removeClusteredClickHandlers(map, sourceId)
  try {
    // Guard against destroyed map — getStyle() throws after map.remove()
    if (!map.getStyle()) return
  } catch {
    return
  }
  try {
    if (map.getLayer(`${sourceId}-clusters`)) {
      map.removeLayer(`${sourceId}-clusters`)
    }
    if (map.getLayer(`${sourceId}-cluster-count`)) {
      map.removeLayer(`${sourceId}-cluster-count`)
    }
    if (map.getLayer(`${sourceId}-unclustered-point`)) {
      map.removeLayer(`${sourceId}-unclustered-point`)
    }
    if (map.getSource(sourceId)) {
      map.removeSource(sourceId)
    }
  } catch (error) {
    console.error('Error removing points:', error)
  }
}

export const updateClusteredPointsOnMap = (
  map: mapboxgl.Map,
  points: ExtendedGeoPoint[],
  fitMapToPoints: boolean = true,
  sourceId: string = 'clustered-points',
  buildMultiCasePopup?: (cases: ExtendedGeoPoint[], totalCaseCount: number) => React.ReactNode,
): void => {
  try {
    // Remove existing points first
    removeClusteredPointsFromMap(map, sourceId)

    // Add new source
    map.addSource(sourceId, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: points.map((point, index) => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [point.lon, point.lat],
          },
          properties: {
            id: index,
            case_count: point.caseCount || 1,
            ...point.customData,
          },
        })),
      },
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50,
      clusterProperties: {
        // Sum up the case_count properties for each point in the cluster
        sum_case_count: ['+', ['get', 'case_count']],
      },
    })

    // Add clusters layer
    map.addLayer({
      id: `${sourceId}-clusters`,
      type: 'circle',
      source: sourceId,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'interpolate',
          ['linear'],
          ['get', 'point_count'],
          10,
          'rgba(0, 255, 255, 0.7)',
          50,
          'rgba(0, 255, 0, 0.7)',
          250,
          'rgba(255, 255, 0, 0.7)',
        ],
        'circle-radius': [
          'interpolate',
          ['linear'],
          ['get', 'point_count'],
          10,
          15,
          50,
          25,
          250,
          35,
        ],
        'circle-stroke-width': 3,
        'circle-stroke-color': 'rgba(255, 255, 255, 0.5)',
      },
    })

    // Calculate total case count for clusters
    map.addLayer({
      id: `${sourceId}-cluster-count`,
      type: 'symbol',
      source: sourceId,
      filter: ['has', 'point_count'],
      layout: {
        // Display the sum of case counts
        // We're using point_count as a fallback, but our implementation uses properties.case_count
        'text-field': '{sum_case_count}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12,
      },
      paint: {
        'text-color': 'black',
      },
    })

    // Add unclustered point layer
    map.addLayer({
      id: `${sourceId}-unclustered-point`,
      type: 'circle',
      source: sourceId,
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': 'rgba(0, 255, 255, 0.7)',
        'circle-radius': 5,
        'circle-stroke-width': 1,
        'circle-stroke-color': 'white',
      },
    })

    // Add popups for unclustered points
    const pointClickHandler: LayerClickHandler = (e) => {
      if (!e.features || e.features.length === 0) return

      const feature = e.features[0]
      if (feature.geometry.type !== 'Point') return

      const coordinates = feature.geometry.coordinates.slice() as [number, number]
      const point = points[feature.properties?.id]
      if (point) showPointPopup(map, point, coordinates, sourceId)
    }

    const clusterClickHandler: LayerClickHandler = (e) => {
      if (!e.features || e.features.length === 0) return

      const feature = e.features[0]
      if (!feature.properties || !feature.geometry || feature.geometry.type !== 'Point') return

      const clusterId = feature.properties.cluster_id
      const pointGeometry = feature.geometry as GeoJSON.Point
      const coordinates = pointGeometry.coordinates.slice() as [number, number]

      const source = map.getSource(sourceId) as mapboxgl.GeoJSONSource
      source.getClusterLeaves(
        clusterId,
        Math.min(feature.properties.point_count ?? MAX_CLUSTER_LEAVES, MAX_CLUSTER_LEAVES),
        0, // Offset
        (err, clusterFeatures) => {
          if (err) {
            console.error('Error getting cluster leaves:', err)
            return
          }

          if (!clusterFeatures) return

          const clusterPoints = clusterFeatures
            .map((feature) => {
              if (!feature.properties) return null
              const pointId = feature.properties.id
              return points[pointId]
            })
            .filter(
              (point): point is ExtendedGeoPoint =>
                point !== null &&
                point !== undefined &&
                point.popupContent !== undefined &&
                point.popupContent !== null,
            )

          if (buildMultiCasePopup) {
            const totalCaseCount =
              feature.properties?.sum_case_count ?? feature.properties?.point_count ?? 0
            const popupContent = buildMultiCasePopup(clusterPoints, totalCaseCount)
            if (popupContent !== null && popupContent !== undefined) {
              openPopup(map, popupContent, coordinates, sourceId)
            }
            return
          }

          showMultiCasePopup(map, clusterPoints, coordinates, sourceId)
        },
      )
    }

    map.on('click', `${sourceId}-unclustered-point`, pointClickHandler)
    map.on('click', `${sourceId}-clusters`, clusterClickHandler)
    registerClusteredClickHandlers(map, sourceId, [pointClickHandler, clusterClickHandler])

    // Fit bounds if needed
    if (fitMapToPoints && points.length > 0) {
      const bounds = new mapboxgl.LngLatBounds()
      points.forEach((point) => bounds.extend([point.lon, point.lat]))
      map.fitBounds(bounds, {
        padding: 50,
        duration: 1500,
        essential: true,
        maxZoom: 15,
      })
    }
  } catch (error) {
    console.error('Error updating points:', error)
  }
}

export interface GroupedMapPoint<T> {
  items: T[]
  lon: number
  lat: number
}

export function buildGroupedMapPoints<T>(
  items: T[],
  getGroupKey: (item: T) => string,
  getLocation: (item: T) => { lon: number; lat: number },
): Record<string, GroupedMapPoint<T>[]> {
  const itemsByGroupAndCoord: Record<string, Record<string, T[]>> = {}
  const groupsPerCoord: Record<string, Set<string>> = {}

  items.forEach((item) => {
    const groupKey = getGroupKey(item)
    const { lon, lat } = getLocation(item)
    const coordKey = `${lat.toFixed(6)},${lon.toFixed(6)}`
    if (!itemsByGroupAndCoord[groupKey]) itemsByGroupAndCoord[groupKey] = {}
    if (!itemsByGroupAndCoord[groupKey][coordKey]) itemsByGroupAndCoord[groupKey][coordKey] = []
    itemsByGroupAndCoord[groupKey][coordKey].push(item)
    if (!groupsPerCoord[coordKey]) groupsPerCoord[coordKey] = new Set()
    groupsPerCoord[coordKey].add(groupKey)
  })

  const result: Record<string, GroupedMapPoint<T>[]> = {}
  for (const [groupKey, coordMap] of Object.entries(itemsByGroupAndCoord)) {
    result[groupKey] = []
    for (const [coordKey, itemsAtCoord] of Object.entries(coordMap)) {
      const { lon, lat } = getLocation(itemsAtCoord[0])
      const groupsAtCoord = groupsPerCoord[coordKey]
      const totalGroups = groupsAtCoord?.size ?? 1
      const groupIdx = Array.from(groupsAtCoord ?? []).indexOf(groupKey)
      let offsetLon = lon
      let offsetLat = lat
      if (totalGroups > 1) {
        const OFFSET = 0.00008
        const angle = (2 * Math.PI * groupIdx) / totalGroups
        offsetLon = lon + OFFSET * Math.cos(angle)
        offsetLat = lat + OFFSET * Math.sin(angle)
      }
      result[groupKey].push({ items: itemsAtCoord, lon: offsetLon, lat: offsetLat })
    }
  }
  return result
}

export type GroupedPointsConfig = Record<
  string,
  {
    points: ExtendedGeoPoint[]
    color: string
    iconHtml: string
    buildMultiCasePopup?: (cases: ExtendedGeoPoint[], totalCaseCount?: number) => React.ReactNode
    clustersClickable?: boolean
  }
>

type GroupedMarkersState = {
  markerCache: Record<string, mapboxgl.Marker>
  markersOnScreenRef: { current: Record<string, mapboxgl.Marker> }
  renderHandler: () => void
}

const groupedMarkersStateMap = new Map<string, GroupedMarkersState>()

export const removeGroupedClusteredPointsFromMap = (
  map: mapboxgl.Map,
  groupKeys: string[],
): void => {
  for (const key of groupKeys) {
    const sourceId = `grouped-${key}`

    const state = groupedMarkersStateMap.get(sourceId)
    if (state) {
      try {
        map.off('render', state.renderHandler)
      } catch {}
      Object.values(state.markersOnScreenRef.current).forEach((m) => m.remove())
      Object.values(state.markerCache).forEach((m) => m.remove())
      groupedMarkersStateMap.delete(sourceId)
    }

    try {
      removeClusteredPointsFromMap(map, sourceId)
    } catch {}
  }
}

export const updateGroupedClusteredPointsOnMap = (
  map: mapboxgl.Map,
  groupedPoints: GroupedPointsConfig,
  fitMapToPoints: boolean = true,
): void => {
  try {
    const groupKeys = Object.keys(groupedPoints)
    removeGroupedClusteredPointsFromMap(map, groupKeys)

    const allPoints: ExtendedGeoPoint[] = []

    for (const [
      groupKey,
      { points, color, iconHtml, buildMultiCasePopup, clustersClickable = true },
    ] of Object.entries(groupedPoints)) {
      if (points.length === 0) continue

      allPoints.push(...points)
      const sourceId = `grouped-${groupKey}`

      map.addSource(sourceId, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: points.map((point, index) => ({
            type: 'Feature' as const,
            geometry: {
              type: 'Point' as const,
              coordinates: [point.lon, point.lat],
            },
            properties: {
              id: index,
              case_count: point.caseCount || 1,
              ...point.customData,
            },
          })),
        },
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50,
        clusterProperties: {
          sum_case_count: ['+', ['get', 'case_count']],
        },
      })
      map.addLayer({
        id: `${sourceId}-clusters`,
        type: 'circle',
        source: sourceId,
        filter: ['has', 'point_count'],
        paint: { 'circle-opacity': 0, 'circle-radius': 0 },
      })
      map.addLayer({
        id: `${sourceId}-unclustered-point`,
        type: 'circle',
        source: sourceId,
        filter: ['!', ['has', 'point_count']],
        paint: { 'circle-opacity': 0, 'circle-radius': 0 },
      })

      const markerCache: Record<string, mapboxgl.Marker> = {}
      const markersOnScreenRef: { current: Record<string, mapboxgl.Marker> } = { current: {} }

      const POINT_MARKER_SIZE = 28
      const CLUSTER_Z_INDEX = 10
      const POINT_Z_INDEX = 1
      const CLUSTER_SIZES = [36, 56, 76] as const

      const getClusterSize = (caseCount: number): number => {
        if (caseCount >= 250) return CLUSTER_SIZES[2]
        if (caseCount >= 50) return CLUSTER_SIZES[1]
        return CLUSTER_SIZES[0]
      }

      const createBaseMarkerElement = (size: number): HTMLDivElement => {
        const el = document.createElement('div')
        Object.assign(el.style, {
          width: `${size}px`,
          height: `${size}px`,
          borderRadius: '50%',
          backgroundColor: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        })
        return el
      }

      const createPointMarkerElement = (): HTMLDivElement => {
        const el = createBaseMarkerElement(POINT_MARKER_SIZE)
        el.style.border = '2px solid white'
        el.style.boxShadow = '0 1px 4px rgba(0,0,0,0.3)'
        el.innerHTML = iconHtml
        return el
      }

      const createClusterMarkerElement = (caseCount: number, hasPopup: boolean): HTMLDivElement => {
        const el = createBaseMarkerElement(getClusterSize(caseCount))
        Object.assign(el.style, {
          opacity: '0.85',
          cursor: hasPopup ? 'pointer' : 'default',
          border: '3px solid rgba(255, 255, 255, 0.7)',
          color: 'white',
          fontSize: '13px',
          fontWeight: '600',
          fontFamily: 'Albert Sans, sans-serif',
        })
        el.textContent = String(caseCount)
        return el
      }

      const setMarkerZIndex = (marker: mapboxgl.Marker, zIndex: number): void => {
        marker.getElement().style.zIndex = String(zIndex)
      }

      const handleClusterClick = (clusterId: number, coords: number[]): void => {
        const source = map.getSource(sourceId) as mapboxgl.GeoJSONSource
        source.getClusterLeaves(clusterId, 1000, 0, (err, clusterFeatures) => {
          if (err || !clusterFeatures) return
          const clusterPoints = clusterFeatures
            .map((f) => (f.properties ? points[f.properties.id] : null))
            .filter(
              (p): p is ExtendedGeoPoint =>
                p !== null &&
                p !== undefined &&
                p.popupContent !== undefined &&
                p.popupContent !== null,
            )
          const caseCount = clusterFeatures.reduce(
            (sum, f) => sum + (f.properties?.case_count || 1),
            0,
          )
          if (buildMultiCasePopup) {
            const popupContent = buildMultiCasePopup(clusterPoints, caseCount)
            if (popupContent !== null && popupContent !== undefined) {
              openPopup(map, popupContent, coords as [number, number], sourceId)
            }
          } else {
            showMultiCasePopup(map, clusterPoints, coords as [number, number], sourceId)
          }
        })
      }

      const handlePointClick = (pointId: number, coords: number[]): void => {
        const point = points[pointId]
        if (point) showPointPopup(map, point, coords as [number, number], sourceId)
      }

      const createOrGetMarker = (
        markerId: string,
        coords: number[],
        element: HTMLDivElement,
      ): mapboxgl.Marker => {
        let marker = markerCache[markerId]
        if (!marker) {
          marker = new mapboxgl.Marker({ element }).setLngLat(coords as [number, number])
          markerCache[markerId] = marker
        }
        return marker
      }

      const renderHandler = (): void => {
        if (!map.isSourceLoaded(sourceId)) return

        const newMarkers: Record<string, mapboxgl.Marker> = {}
        const features = map.querySourceFeatures(sourceId)

        for (const feature of features) {
          if (!feature.properties) continue
          const coords = (feature.geometry as GeoJSON.Point).coordinates

          if (feature.properties.cluster) {
            const clusterId = feature.properties.cluster_id
            const markerId = `${sourceId}-cluster-${clusterId}`
            const caseCount = feature.properties.sum_case_count || feature.properties.point_count

            const el = createClusterMarkerElement(caseCount, clustersClickable)
            if (clustersClickable) {
              el.addEventListener('click', (e) => {
                e.stopPropagation()
                handleClusterClick(clusterId, coords)
              })
            }
            const marker = createOrGetMarker(markerId, coords, el)
            setMarkerZIndex(marker, CLUSTER_Z_INDEX)
            newMarkers[markerId] = marker
            if (!markersOnScreenRef.current[markerId]) marker.addTo(map)
          } else {
            const pointId = feature.properties.id
            const caseCount = feature.properties.case_count || 1
            const markerId = `${sourceId}-${pointId}`

            const isCluster = caseCount > 1
            const el = isCluster
              ? createClusterMarkerElement(caseCount, clustersClickable)
              : createPointMarkerElement()
            if (!isCluster || clustersClickable) {
              el.addEventListener('click', (e) => {
                e.stopPropagation()
                handlePointClick(pointId, coords)
              })
            }
            const marker = createOrGetMarker(markerId, coords, el)
            setMarkerZIndex(marker, caseCount > 1 ? CLUSTER_Z_INDEX : POINT_Z_INDEX)
            newMarkers[markerId] = marker
            if (!markersOnScreenRef.current[markerId]) marker.addTo(map)
          }
        }

        for (const id in markersOnScreenRef.current) {
          if (!newMarkers[id]) markersOnScreenRef.current[id].remove()
        }
        markersOnScreenRef.current = newMarkers
      }

      map.on('render', renderHandler)

      groupedMarkersStateMap.set(sourceId, {
        markerCache,
        markersOnScreenRef,
        renderHandler,
      })
    }

    if (fitMapToPoints && allPoints.length > 0) {
      const bounds = new mapboxgl.LngLatBounds()
      allPoints.forEach((point) => bounds.extend([point.lon, point.lat]))
      map.fitBounds(bounds, {
        padding: 50,
        duration: 1500,
        essential: true,
        maxZoom: 15,
      })
    }
  } catch (error) {
    console.error('Error updating grouped points:', error)
  }
}
